/**
 * Kazakan Tools 2.0 — Shared Reusable Modal & Notification System
 * High-performance, zero-dependency native <dialog> with iOS Frosted Glass UI
 * Fully accessible, mobile responsive, dark/light theme reactive, bilingual (AR/EN)
 */

let modalDialog = null;
let activeResolve = null;

function isArabic() {
  const lang = document.documentElement.lang || '';
  const dir = document.documentElement.dir || '';
  return lang.toLowerCase().startsWith('ar') || dir === 'rtl';
}

function escapeHTML(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[c]);
}

function getIconSvg(type) {
  switch (type) {
    case 'success':
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>`;
    case 'error':
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;
    case 'warning':
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;
    case 'confirm':
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;
    case 'info':
    default:
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`;
  }
}

function ensureModal() {
  if (modalDialog && document.body.contains(modalDialog)) {
    return modalDialog;
  }

  modalDialog = document.createElement('dialog');
  modalDialog.className = 'kz-modal-dialog';
  modalDialog.id = 'kz-modal-dialog';
  modalDialog.setAttribute('aria-labelledby', 'kz-modal-title');
  modalDialog.setAttribute('aria-describedby', 'kz-modal-desc');

  modalDialog.innerHTML = `
    <div class="kz-modal-card" id="kz-modal-card">
      <div class="kz-modal-topline">
        <div class="kz-modal-icon-badge" id="kz-modal-icon-wrap"></div>
        <button type="button" class="kz-modal-close-btn" id="kz-modal-close-btn" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="kz-modal-content">
        <h3 class="kz-modal-title" id="kz-modal-title"></h3>
        <p class="kz-modal-desc" id="kz-modal-desc"></p>
        <div class="kz-modal-prompt-wrap" id="kz-modal-prompt-wrap" hidden>
          <input type="text" class="kz-modal-input" id="kz-modal-input" autocomplete="off" spellcheck="false" />
        </div>
      </div>
      <div class="kz-modal-actions" id="kz-modal-actions">
        <button type="button" class="kz-modal-btn kz-modal-btn-cancel" id="kz-modal-btn-cancel"></button>
        <button type="button" class="kz-modal-btn kz-modal-btn-confirm" id="kz-modal-btn-confirm"></button>
      </div>
    </div>
  `;

  document.body.appendChild(modalDialog);

  const closeBtn = modalDialog.querySelector('#kz-modal-close-btn');
  const cancelBtn = modalDialog.querySelector('#kz-modal-btn-cancel');
  const confirmBtn = modalDialog.querySelector('#kz-modal-btn-confirm');
  const input = modalDialog.querySelector('#kz-modal-input');

  const finish = (result) => {
    if (modalDialog && modalDialog.open) {
      modalDialog.close();
    }
    if (activeResolve) {
      const res = activeResolve;
      activeResolve = null;
      res(result);
    }
  };

  closeBtn.addEventListener('click', () => finish(modalDialog.dataset.isPrompt ? null : false));
  cancelBtn.addEventListener('click', () => finish(modalDialog.dataset.isPrompt ? null : false));

  confirmBtn.addEventListener('click', () => {
    if (modalDialog.dataset.isPrompt) {
      finish(input.value);
    } else {
      finish(true);
    }
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmBtn.click();
    }
  });

  modalDialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    finish(modalDialog.dataset.isPrompt ? null : false);
  });

  modalDialog.addEventListener('click', (e) => {
    const card = modalDialog.querySelector('#kz-modal-card');
    if (card && !card.contains(e.target)) {
      finish(modalDialog.dataset.isPrompt ? null : false);
    }
  });

  return modalDialog;
}

/**
 * Universal Modal Dialog (Alert / Confirm / Prompt)
 * @param {Object} options 
 * @returns {Promise<any>}
 */
export function showModal(options = {}) {
  const isAr = isArabic();
  const {
    title,
    message = '',
    type = 'info', // 'info' | 'success' | 'warning' | 'error' | 'confirm'
    confirmText,
    cancelText,
    confirmVariant = 'primary', // 'primary' | 'danger'
    isPrompt = false,
    defaultValue = '',
    placeholder = '',
    showCancel = false
  } = options;

  const defaultTitles = {
    info: isAr ? 'إشعار' : 'Notice',
    success: isAr ? 'تم بنجاح' : 'Success',
    warning: isAr ? 'تنبيه' : 'Warning',
    error: isAr ? 'تنبيه خطأ' : 'Error',
    confirm: isAr ? 'تأكيد الإجراء' : 'Confirmation'
  };

  const finalTitle = title || defaultTitles[type] || (isAr ? 'إشعار' : 'Notice');
  const finalConfirmText = confirmText || (showCancel ? (isAr ? 'تأكيد' : 'Confirm') : (isAr ? 'حسناً' : 'OK'));
  const finalCancelText = cancelText || (isAr ? 'إلغاء' : 'Cancel');

  const dialog = ensureModal();
  dialog.setAttribute('data-type', type);
  dialog.dataset.isPrompt = isPrompt ? 'true' : '';

  const titleEl = dialog.querySelector('#kz-modal-title');
  const descEl = dialog.querySelector('#kz-modal-desc');
  const iconWrap = dialog.querySelector('#kz-modal-icon-wrap');
  const promptWrap = dialog.querySelector('#kz-modal-prompt-wrap');
  const input = dialog.querySelector('#kz-modal-input');
  const cancelBtn = dialog.querySelector('#kz-modal-btn-cancel');
  const confirmBtn = dialog.querySelector('#kz-modal-btn-confirm');
  const card = dialog.querySelector('#kz-modal-card');

  titleEl.textContent = finalTitle;
  descEl.textContent = message;
  iconWrap.innerHTML = getIconSvg(type);
  iconWrap.className = `kz-modal-icon-badge kz-icon-${type}`;

  if (isPrompt) {
    promptWrap.hidden = false;
    input.value = defaultValue || '';
    input.placeholder = placeholder || '';
  } else {
    promptWrap.hidden = true;
  }

  cancelBtn.style.display = showCancel ? 'inline-flex' : 'none';
  cancelBtn.textContent = finalCancelText;

  confirmBtn.textContent = finalConfirmText;
  confirmBtn.className = `kz-modal-btn kz-modal-btn-confirm ${confirmVariant === 'danger' ? 'is-danger' : 'is-primary'}`;

  card.setAttribute('data-type', type);

  return new Promise((resolve) => {
    activeResolve = resolve;
    if (!dialog.open) {
      dialog.showModal();
    }
    if (isPrompt) {
      input.focus();
      input.select();
    } else {
      confirmBtn.focus();
    }
  });
}

/**
 * Modern replacement for alert()
 * @param {string} message 
 * @param {Object} options 
 * @returns {Promise<boolean>}
 */
export function showAlert(message, options = {}) {
  const type = options.type || 'info';
  return showModal({
    message,
    type,
    showCancel: false,
    ...options
  });
}

/**
 * Modern replacement for confirm()
 * @param {string} message 
 * @param {Object} options 
 * @returns {Promise<boolean>}
 */
export function showConfirm(message, options = {}) {
  return showModal({
    message,
    type: options.type || 'confirm',
    showCancel: true,
    ...options
  });
}

/**
 * Modern replacement for prompt()
 * @param {string} message 
 * @param {string} defaultValue 
 * @param {Object} options 
 * @returns {Promise<string|null>}
 */
export function showPrompt(message, defaultValue = '', options = {}) {
  return showModal({
    message,
    type: options.type || 'info',
    showCancel: true,
    isPrompt: true,
    defaultValue,
    ...options
  });
}

/**
 * Lightweight Toast Notification (Auto dismisses)
 * @param {string} message 
 * @param {Object} options 
 */
export function showToast(message, options = {}) {
  const type = options.type || 'info';
  const duration = options.duration || 3200;
  let toastContainer = document.getElementById('kz-toast-container');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'kz-toast-container';
    toastContainer.className = 'kz-toast-container';
    toastContainer.setAttribute('aria-live', 'polite');
    toastContainer.setAttribute('aria-atomic', 'true');
    document.body.appendChild(toastContainer);
  }

  const toast = document.createElement('div');
  toast.className = `kz-toast-item kz-toast-${type}`;
  toast.innerHTML = `
    <span class="kz-toast-icon" aria-hidden="true">${getIconSvg(type)}</span>
    <span class="kz-toast-msg">${escapeHTML(message)}</span>
  `;
  toastContainer.appendChild(toast);

  const remove = () => {
    toast.classList.add('is-leaving');
    setTimeout(() => {
      toast.remove();
      if (toastContainer && toastContainer.children.length === 0) {
        toastContainer.remove();
      }
    }, 220);
  };

  const timer = setTimeout(remove, duration);
  toast.addEventListener('click', () => {
    clearTimeout(timer);
    remove();
  });
}

// Global browser bridge
if (typeof window !== 'undefined') {
  window.kzModal = {
    showAlert,
    showConfirm,
    showPrompt,
    showToast,
    show: showModal
  };

  // Safe fallback polyfills: prevents default browser popups anywhere across the site
  window.alert = function (msg) {
    showAlert(String(msg || ''));
  };

  window.confirm = function (msg) {
    console.warn('Native window.confirm() intercepted. Use showConfirm() from core/modal.js');
    showConfirm(String(msg || ''));
    return false;
  };

  window.prompt = function (msg, defaultVal) {
    console.warn('Native window.prompt() intercepted. Use showPrompt() from core/modal.js');
    showPrompt(String(msg || ''), String(defaultVal || ''));
    return null;
  };
}
